import axios from "axios";

const STATUS_MESSAGES: Record<number, string> = {
  400: "Some information is missing or invalid. Please check the form and try again.",
  401: "Your session has expired. Please sign in again.",
  403: "You do not have permission to perform this action.",
  404: "The requested project or result could not be found.",
  409: "This item already exists or conflicts with existing data.",
  413: "The selected file is too large to upload.",
  429: "Too many requests. Please wait a moment and try again.",
  500: "The analysis could not be completed because of a server error. Please try again.",
  502: "The analysis service is temporarily unavailable. Please try again shortly.",
  503: "The analysis service is temporarily unavailable. Please try again shortly.",
};

const safeBackendMessage = (data: any): string | null => {
  const candidate = data?.message ?? data?.error ?? data?.detail;
  if (typeof candidate !== "string") return null;
  const value = candidate.trim();
  if (!value || value.length > 300) return null;
  if (/traceback|stack trace|sqlstate|\/code\/|file "/i.test(value)) return null;
  return value;
};

export const getApiErrorMessage = (
  response?: { status?: number; data?: any } | null,
  fallback = "Something went wrong. Please try again.",
): string => {
  const status = response?.status;
  const backendMessage = safeBackendMessage(response?.data);
  if (backendMessage) return backendMessage;
  if (status && STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];
  return status ? fallback : "Could not connect to the server. Please check your connection and try again.";
};

// TODO: Add token management and refresh logic when needed
// - Implement userStore integration
// - Add token refresh functionality
// - Handle token expiration and refresh
// - Add proper authentication headers

const api = axios.create({
  baseURL: "",
  withCredentials: true,
  headers: { accept: "application/json", "Content-Type": "application/json" },
});

// In-memory token store (source of truth for request auth)
let authToken: string | null = null;

export const setAuthToken = (token: string | null) => {
  authToken = token;
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common.Authorization;
  }
};

// Attach Authorization header automatically on every request
api.interceptors.request.use((config) => {
  if (authToken) {
    // Ensure headers object exists and set Authorization in a type-safe way
    config.headers = config.headers ?? {};
    (config.headers as any).Authorization = `Bearer ${authToken}`;
  }
  return config;
});

const handleError = async (
  error: any,
  errorCallback?: (error: any) => void,
) => {
  if (errorCallback) {
    errorCallback(error);
  }

  // Return the error response so it can be handled by the calling function
  if (error.response) {
    return {
      status: error.response.status,
      data: error.response.data,
    };
  }

  // If no response, throw the error
  throw error;
};

const get = async <T>(
  url: string,
  errorCallback?: (error: any) => void,
  options?: { [key: string]: any },
) => {
  try {
    const response = await api.get<T>(`${url}`, {
      ...options,
      headers: { ...options?.headers },
    });
    return { status: response.status, data: response.data };
  } catch (error: any) {
    const errorResponse = await handleError(error, errorCallback);
    return errorResponse as { status: number; data: T };
  }
};

const post = async <T>(
  url: string,
  data: any,
  errorCallback?: (error: any) => void,
  options?: { [key: string]: any },
) => {
  try {
    const response = await api.post<T>(`${url}`, data, {
      ...options,
      headers: { ...options?.headers },
    });
    return { status: response.status, data: response.data };
  } catch (error: any) {
    const errorResponse = await handleError(error, errorCallback);
    return errorResponse as { status: number; data: T };
  }
};

const remove = async <T>(
  url: string,
  errorCallback?: (error: any) => void,
  options?: { [key: string]: any },
) => {
  try {
    const response = await api.delete<T>(`${url}`, {
      ...options,
      headers: { ...options?.headers },
    });
    return { status: response.status, data: response.data };
  } catch (error: any) {
    const errorResponse = await handleError(error, errorCallback);
    return errorResponse as { status: number; data: T };
  }
};

const patch = async <T>(
  url: string,
  data: any,
  errorCallback?: (error: any) => void,
  options?: { [key: string]: any },
) => {
  try {
    const response = await api.patch<T>(`${url}`, data, {
      ...options,
      headers: { ...options?.headers },
    });
    return { status: response.status, data: response.data };
  } catch (error: any) {
    const errorResponse = await handleError(error, errorCallback);
    return errorResponse as { status: number; data: T };
  }
};

const put = async <T>(
  url: string,
  data: any,
  errorCallback?: (error: any) => void,
  options?: { [key: string]: any },
) => {
  try {
    const response = await api.put<T>(`${url}`, data, {
      ...options,
      headers: { ...options?.headers },
    });
    return { status: response.status, data: response.data };
  } catch (error: any) {
    const errorResponse = await handleError(error, errorCallback);
    return errorResponse as { status: number; data: T };
  }
};

const apiService = { get, post, patch, delete: remove, put };

export default apiService;
